import { z } from "zod";
import { ImageKitUrlPrefix } from "../types/Attachment";
import { joinWith } from "./array.util";
import { isTypeOf } from "./zod.util";

const storyblokAmazonDomain = "https://s3.amazonaws.com/a.storyblok.com";
const storyblokCdnDomain = "https://a.storyblok.com";

// https://docs.imagekit.io/features/image-transformations
function transformImageKitUrl(
    url: string,
    options?: { height?: number; width?: number },
): string {
    const { height, width } = options || {};
    const urlParts = url.split("/");
    const filename = urlParts.pop();
    const path = urlParts.join("/");
    return `${path}/tr:f-avif${width ? `,w-${width}` : ""}${height ? `,h-${height}` : ""}/${filename}`;
}

export function cdnImage(
    filename: string,
    options?: { height?: number; width?: number },
) {
    if (filename.startsWith(ImageKitUrlPrefix)) {
        return transformImageKitUrl(filename, options);
    }

    if (
        !filename.startsWith(storyblokCdnDomain) &&
        !filename.startsWith(storyblokAmazonDomain)
    ) {
        return filename;
    }

    if (
        filename.includes(storyblokAmazonDomain) ||
        filename.includes(storyblokCdnDomain)
    ) {
        // https://www.storyblok.com/docs/image-service/#resizing
        const imageResize = options
            ? `${options.width || 0}x${options.height || 0}`
            : "";

        return `${filename.replace(
            storyblokAmazonDomain,
            storyblokCdnDomain,
        )}/m/${imageResize}`;
    }

    return filename;
}

export const SupportedImageMimeType = z.enum([
    "image/avif",
    "image/jpeg",
    "image/png",
    "image/webp",
]);
export type SupportedImageMimeType = z.infer<typeof SupportedImageMimeType>;
export const supportedImageTypes = SupportedImageMimeType.options;

const extensionToMimeType = {
    avif: "image/avif",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
} as const satisfies Record<string, SupportedImageMimeType>;

const mimeTypeAliases = {
    "image/jpg": "image/jpeg",
    "image/pjpeg": "image/jpeg",
    "image/x-png": "image/png",
} as const satisfies Record<string, SupportedImageMimeType>;

/** File-picker filter for the formats production accepts. Excludes HEIC, GIF, and other image types. */
export const supportedImageAccept = [
    ".avif",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ...supportedImageTypes,
].join(",");

const supportedImageUploadHint = "Upload a JPEG, PNG, WebP, or AVIF image.";

function fileExtension(name: string): string {
    const dot = name.lastIndexOf(".");
    if (dot < 0 || dot === name.length - 1) {
        return "";
    }
    return name.slice(dot + 1).toLowerCase();
}

export function imageFileMimeType(file: File): string {
    const aliased = mimeTypeAliases[file.type as keyof typeof mimeTypeAliases];
    if (aliased) {
        return aliased;
    }
    if (isTypeOf(file.type, SupportedImageMimeType)) {
        return file.type;
    }
    if (file.type && file.type !== "application/octet-stream") {
        return file.type;
    }
    const fromExtension =
        extensionToMimeType[
            fileExtension(file.name) as keyof typeof extensionToMimeType
        ];
    return fromExtension ?? file.type;
}

export function isSupportedImageFile(file: File): boolean {
    return isTypeOf(imageFileMimeType(file), SupportedImageMimeType);
}

export function requireSupportedImageFile(file: File): void {
    if (!isSupportedImageFile(file)) {
        throw new Error(unsupportedImageUploadMessage([file]));
    }
}

export function partitionSupportedImageFiles(files: FileList | readonly File[]): {
    supported: File[];
    rejected: File[];
} {
    const supported: File[] = [];
    const rejected: File[] = [];
    for (const file of Array.from(files)) {
        if (isSupportedImageFile(file)) {
            supported.push(file);
        } else {
            rejected.push(file);
        }
    }
    return { supported, rejected };
}

export function imageFormatLabel(file: File): string {
    const extension = fileExtension(file.name);
    if (extension) {
        return extension === "jpg" ? "JPEG" : extension.toUpperCase();
    }
    if (file.type.startsWith("image/")) {
        const subtype = file.type.slice("image/".length).split("+")[0] ?? "";
        if (subtype === "jpeg" || subtype === "jpg") {
            return "JPEG";
        }
        if (subtype) {
            return subtype.toUpperCase();
        }
    }
    return "This file";
}

export function unsupportedImageUploadMessage(files: readonly File[]): string {
    const labels = [...new Set(files.map((file) => imageFormatLabel(file)))];
    if (labels.length === 1) {
        const label = labels[0] ?? "This file";
        return `${label} is not supported. ${supportedImageUploadHint}`;
    }
    return `${joinWith(labels, ", ", "and")} are not supported. ${supportedImageUploadHint}`;
}

export function mimeTypeToExtension(
    mimeType: string,
): "jpeg" | "png" | "webp" | "avif" | undefined {
    if (!isTypeOf(mimeType, SupportedImageMimeType)) {
        return undefined;
    }

    switch (mimeType) {
        case "image/avif":
            return "avif";
        case "image/jpeg":
            return "jpeg";
        case "image/png":
            return "png";
        case "image/webp":
            return "webp";
        default:
            return undefined;
    }
}
